// components/Manager/Order/Action/SplitOrderForManager.jsx
import React, { useEffect, useMemo, useState } from "react"
import {
  Button,
  Descriptions,
  Divider,
  Drawer,
  InputNumber,
  Space,
  Typography,
  message,
} from "antd"
import { SplitCellsOutlined } from "@ant-design/icons"
import { collection, doc, serverTimestamp, runTransaction } from "firebase/firestore"
import { db } from "../../../../firebase/config"

const { Text } = Typography

export default function SplitOrderForManager({ selectedRows, onDone }) {
  const [open, setOpen] = useState(false)
  const [firstBoxes, setFirstBoxes] = useState()
  const [firstAmount, setFirstAmount] = useState()
  const [secondBoxes, setSecondBoxes] = useState()
  const [secondAmount, setSecondAmount] = useState()
  const [submitting, setSubmitting] = useState(false)

  const selected = useMemo(
    () => (Array.isArray(selectedRows) && selectedRows.length === 1 ? selectedRows[0] : null),
    [selectedRows]
  )
  const isAlreadySplit = !!selected?.splitOrder

  const originalBoxes = Number(selected?.boxesCount ?? 0)
  const originalAmount = Number(selected?.amountCreated ?? 0)

  useEffect(() => {
    const fb = firstBoxes === undefined ? undefined : Number(firstBoxes)
    const fa = firstAmount === undefined ? undefined : Number(firstAmount)

    setSecondBoxes(
      fb === undefined || !Number.isFinite(originalBoxes) ? undefined : originalBoxes - fb
    )
    setSecondAmount(
      fa === undefined || !Number.isFinite(originalAmount) ? undefined : originalAmount - fa
    )
  }, [firstBoxes, firstAmount, originalBoxes, originalAmount])

  const firstBoxesNum = Number(firstBoxes)
  const firstAmountNum = Number(firstAmount)
  const secondBoxesNum = Number(secondBoxes)
  const secondAmountNum = Number(secondAmount)

  const valuesAreDefined =
    firstBoxes !== undefined &&
    firstAmount !== undefined &&
    secondBoxes !== undefined &&
    secondAmount !== undefined

  const canSubmit =
    !!selected &&
    !isAlreadySplit &&
    Number.isFinite(originalBoxes) &&
    Number.isFinite(originalAmount) &&
    valuesAreDefined &&
    Number.isFinite(firstBoxesNum) &&
    Number.isFinite(firstAmountNum) &&
    Number.isFinite(secondBoxesNum) &&
    Number.isFinite(secondAmountNum) &&
    // allow zero now:
    firstBoxesNum >= 0 &&
    firstAmountNum >= 0 &&
    secondBoxesNum >= 0 &&
    secondAmountNum >= 0 &&
    // within bounds and sums match:
    firstBoxesNum + secondBoxesNum === originalBoxes &&
    firstAmountNum + secondAmountNum === originalAmount

  const handleOpen = () => {
    if (!selected) {
      message.warning("Select exactly one order to split.")
      return
    }
    if (isAlreadySplit) {
      message.warning("This order is already split. Combine it first, then split again if needed.")
      return
    }
    if (!Number.isFinite(originalBoxes) || !Number.isFinite(originalAmount)) {
      message.error("This order has invalid boxesCount or amountCreated.")
      return
    }
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
    setFirstBoxes(undefined)
    setFirstAmount(undefined)
    setSecondBoxes(undefined)
    setSecondAmount(undefined)
  }

  const doSplit = async () => {
    try {
      if (!selected?.id) {
        message.error("Order is missing its 'id'.")
        return
      }

      const fb = Number(firstBoxes)
      const fa = Number(firstAmount)
      const sb = Number(secondBoxes)
      const sa = Number(secondAmount)

      // allow zero, disallow negatives / NaN:
      if (
        !Number.isFinite(fb) ||
        !Number.isFinite(fa) ||
        !Number.isFinite(sb) ||
        !Number.isFinite(sa) ||
        fb < 0 ||
        fa < 0 ||
        sb < 0 ||
        sa < 0
      ) {
        message.error("All counts must be numbers and cannot be negative.")
        return
      }
      if (fb + sb !== originalBoxes || fa + sa !== originalAmount) {
        message.error("First + second must equal the original values for both fields.")
        return
      }

      setSubmitting(true)

      await runTransaction(db, async (tx) => {
        const oldRef = doc(db, "orders", selected.id)
        const snap = await tx.get(oldRef)
        if (!snap.exists()) throw new Error("Order no longer exists.")
        const current = snap.data()

        if (current.splitOrder === true) {
          throw new Error(
            "This order was already split. Combine first, then split again if needed."
          )
        }

        const { id: _ignore, ...clone } = { ...current, id: selected.id }
        const newRef = doc(collection(db, "orders"))
        const splitTime = serverTimestamp()

        // New doc (part 1)
        tx.set(newRef, {
          ...clone,
          boxesCount: fb,
          amountCreated: fa,
          splitOrder: true,
          partOfSplit: 1,
          totalParts: 2,
          otherOrderId: selected.id,
          beforeSplitBoxesCount: originalBoxes,
          beforeSplitAmountCreated: originalAmount,
          splitTime,
          splitRootId: current.splitRootId || selected.id,
          splitParentId: selected.id,
        })

        // Old doc (part 2)
        tx.update(oldRef, {
          boxesCount: sb,
          amountCreated: sa,
          splitOrder: true,
          partOfSplit: 2,
          totalParts: 2,
          otherOrderId: newRef.id,
          beforeSplitBoxesCount: originalBoxes,
          beforeSplitAmountCreated: originalAmount,
          splitTime,
          splitRootId: current.splitRootId || selected.id,
          splitParentId: selected.id,
        })
      })

      message.success("Order split completed!")
      handleClose()
      onDone?.()
    } catch (err) {
      console.error(err)
      message.error(err?.message || "Failed to split order. Check console for details.")
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      <Button
        icon={<SplitCellsOutlined />}
        type="primary"
        onClick={handleOpen}
        disabled={
          !Array.isArray(selectedRows) ||
          selectedRows.length !== 1 ||
          !!selectedRows?.[0]?.splitOrder
        }>
        Split Selected
      </Button>

      <Drawer
        width={720}
        title={
          selected ? `Split order ${selected.orderNumber || "#" + selected.id}` : "Split order"
        }
        placement="right"
        onClose={handleClose}
        open={open}
        extra={
          <Space>
            <Button onClick={handleClose}>Cancel</Button>
            <Button type="primary" loading={submitting} disabled={!canSubmit} onClick={doSplit}>
              Confirm Split
            </Button>
          </Space>
        }>
        {selected ? (
          <>
            <Divider>What will be split</Divider>
            <Text>
              This creates a <b>new order</b> identical to the current one, but you divide
              <b> boxesCount</b> and <b>amountCreated</b> between them. <br />
              <i>The planned labelCount is not changed.</i>
            </Text>

            <Divider>Split Details</Divider>
            <Descriptions column={2} bordered size="small">
              <Descriptions.Item label="Original boxesCount" span={1}>
                <b>{originalBoxes}</b>
              </Descriptions.Item>
              <Descriptions.Item label="Original amountCreated" span={1}>
                <b>{originalAmount}</b>
              </Descriptions.Item>

              <Descriptions.Item label="New order boxesCount (first)" span={1}>
                <InputNumber
                  style={{ width: "100%" }}
                  min={0}
                  max={originalBoxes}
                  value={firstBoxes}
                  onChange={(v) => setFirstBoxes(v)}
                />
              </Descriptions.Item>
              <Descriptions.Item label="New order amountCreated (first)" span={1}>
                <InputNumber
                  style={{ width: "100%" }}
                  min={0}
                  max={originalAmount}
                  value={firstAmount}
                  onChange={(v) => setFirstAmount(v)}
                />
              </Descriptions.Item>

              <Descriptions.Item label="Remaining boxesCount (second)" span={1}>
                <InputNumber style={{ width: "100%" }} value={secondBoxes} disabled />
              </Descriptions.Item>
              <Descriptions.Item label="Remaining amountCreated (second)" span={1}>
                <InputNumber style={{ width: "100%" }} value={secondAmount} disabled />
              </Descriptions.Item>
            </Descriptions>
          </>
        ) : (
          <Text type="secondary">Select exactly one order to split.</Text>
        )}
      </Drawer>
    </>
  )
}
